/* eslint-disable no-dupe-class-members */
import { BehaviorSubject, of, AsyncSubject, Observable, Subscription, forkJoin, PartialObserver, Subscribable } from 'rxjs';
import { switchMap, catchError, distinctUntilChanged, skipUntil, skip, take, map, filter, pairwise, startWith, mapTo } from 'rxjs/operators';
import eq from 'lodash/eq';
import find from 'lodash/find';
import get from 'lodash/get';

import doGetUser from './services/Dalton/api/doGetUser';
import doGetIdentities from './services/Dalton/api/doGetIdentities';
import AuthToken, { AuthTokenValue, AuthTokenRawValue } from './services/Dalton/AuthToken';
import doLogout from './services/Dalton/api/doLogout';
import { parseDateString } from './utils/dateUtils';
import IdentityType from './services/Dalton/constants/IdentityType';
import Authorization from './services/Dalton/constants/Authorization';
import { checkAuthorization } from './services/Dalton/utils';
import { IdentityResponse } from './services/Dalton';

interface AuthTokenLike {
    asObservable(authz?: string[]): Observable<AuthTokenValue>;
    setValue(value: any): void;
    getValue(): AuthTokenRawValue;
}

export interface UserData {
    UID: string;
    attributes: Record<string, any>;
    birthdate: Date | null;
    emailAddress: string | null;
    identities: IdentityResponse[];
    isRegistered: boolean;
    isVerified: boolean;
    username: string | null;
}

const NO_USER = of(null);

export function isUserData(user: UserData | null): user is UserData {
    return user !== null;
}

function fetchUser(authToken: AuthTokenValue) {
    if (!authToken) {
        return NO_USER;
    }
    return forkJoin(
        doGetUser(authToken.rawToken),
        doGetIdentities(authToken.rawToken),
    ).pipe(
        map(([profile, identities]): UserData | null => {
            const email = find(profile.userEmailResponses, { primary: true });
            const internal = find(identities, { identityType: IdentityType._INTERNAL });

            if (!internal) {
                return null;
            }

            const { id } = internal;
            const attrs: Record<string, string> = get(profile, 'userProfileResponses.adultswim.attributes', {});
            const username = typeof attrs.username === 'string' && attrs.username.toLowerCase() === id.toLowerCase() ? attrs.username : id;

            return {
                UID: profile.tid,
                attributes: attrs,
                birthdate: parseDateString(typeof profile.birthdate === 'string' ? profile.birthdate : ''),
                emailAddress: email && typeof email.emailAddress === 'string' ? email.emailAddress : null,
                identities: identities,
                isRegistered: checkAuthorization(authToken, Authorization.REGISTERED),
                isVerified: checkAuthorization(authToken, Authorization.VERIFIED),
                username,
            };
        }),
        catchError(() => NO_USER)
    );
}

class User implements Subscribable<UserData | null> {
    public readonly authToken: AuthTokenLike;
    private readonly _readyState$ = new AsyncSubject<boolean>();
    private readonly _value$ = new BehaviorSubject<UserData | null>(null);

    public constructor(authToken: AuthTokenLike = AuthToken) {
        this.authToken = authToken;

        // set ready state as soon as user data receives first next value
        this._value$.pipe(skip(1), take(1)).subscribe(() => {
            this._readyState$.next(true);
            this._readyState$.complete();
        });

        // subscribe to auth token and get user profile
        this.authToken.asObservable([Authorization.AUTHN])
            .pipe(switchMap(fetchUser))
            .subscribe(this._value$);
    }

    public [Symbol.observable]() {
        return this.asObservable();
    }

    /**
     * Returns an Observable of the user data or `null`.
     * @returns {(Observable<UserData | null>)}
     */
    public asObservable(): Observable<UserData | null> {
        return this._value$.asObservable().pipe(
            skipUntil(this._readyState$),
            distinctUntilChanged(eq),
        );
    }

    /**
     * Returns a Promise of the user data or `null`.
     * @returns {(Promise<UserData | null>)}
     */
    public getValue(): Promise<UserData | null> {
        return this.asObservable().pipe(take(1)).toPromise();
    }

    /**
     * Attempts to log out the current user
     * @returns {Promise<void>}
     */
    public async logout(): Promise<void> {
        try {
            await doLogout(this.authToken.getValue());
        // eslint-disable-next-line no-empty
        } catch (error) {}

        this.authToken.setValue(null);
    }

    /**
     * Returns an Observable of the user data when the user logs in
     * @returns {Observable<UserData>}
     */
    public onLogin(): Observable<UserData> {
        return this.asObservable().pipe(
            startWith(null),
            pairwise(),
            map(([a, b]) => !a && b ? b : null),
            filter(isUserData)
        );
    }

    /**
     * Returns an Observable of the user data when the user logs in
     * @returns {Observable<UserData>}
     */
    public onLogout(): Observable<void> {
        return this.asObservable().pipe(
            startWith(null),
            pairwise(),
            filter(([a, b]) => !!a && !b),
            mapTo(undefined)
        );
    }

    /**
     * Returns the first value only of the user data as soon as its ready.
     * If a callback function is passed, returns the Subscription object
     */
    public onReady(): Observable<UserData | null>;
    public onReady(onNext: (data: UserData | null) => void): Subscription;
    public onReady(onNext?: (data: UserData | null) => void): Observable<UserData | null> | Subscription {
        const userData$ = this.asObservable().pipe(take(1));
        return typeof onNext === 'undefined' ? userData$ : userData$.subscribe(onNext);
    }

    public subscribe(): Subscription;
    public subscribe(observer: PartialObserver<UserData | null>): Subscription;
    public subscribe(next?: (value: UserData | null) => void, error?: (error: any) => void, complete?: () => void): Subscription;
    public subscribe(observerOrNext?: PartialObserver<UserData | null> | ((value: UserData | null) => void), error?: (error: any) => void, complete?: () => void): Subscription {
        if (typeof observerOrNext === 'function') {
            return this.asObservable().subscribe(observerOrNext, error, complete);
        } else {
            return this.asObservable().subscribe(observerOrNext);
        }
    }
}

export default new User();
